"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MintWrapper = void 0;
const token_utils_1 = require("@saberhq/token-utils");
const web3_js_1 = require("@solana/web3.js");
const pda_1 = require("./pda");
class MintWrapper {
    constructor(sdk) {
        this.sdk = sdk;
        this.program = sdk.programs.MintWrapper;
    }
    get provider() {
        return this.sdk.provider;
    }
    async newWrapperAndMintV1({ mintKP = web3_js_1.Keypair.generate(), decimals = 6, ...newWrapperArgs }) {
        const provider = this.provider;
        const { mintWrapper, tx: initMintProxyTX } = await this.newWrapperV1({
            ...newWrapperArgs,
            tokenMint: mintKP.publicKey,
        });
        const initMintTX = await (0, token_utils_1.createInitMintInstructions)({
            provider,
            mintAuthority: mintWrapper,
            freezeAuthority: mintWrapper,
            mintKP,
            decimals,
        });
        return {
            mintWrapper,
            mint: mintKP.publicKey,
            tx: initMintTX.combine(initMintProxyTX),
        };
    }
    async newWrapperV1({ hardcap, tokenMint, baseKP = web3_js_1.Keypair.generate(), tokenProgram = token_utils_1.TOKEN_PROGRAM_ID, admin = this.provider.wallet.publicKey, payer = this.provider.wallet.publicKey, }) {
        const [mintWrapper, bump] = await (0, pda_1.findMintWrapperAddress)(baseKP.publicKey, this.program.programId);
        return {
            mintWrapper,
            tx: this.provider.newTX([
                this.program.instruction.newWrapper(bump, hardcap, {
                    accounts: {
                        base: baseKP.publicKey,
                        mintWrapper,
                        admin,
                        tokenMint,
                        tokenProgram,
                        payer,
                        systemProgram: web3_js_1.SystemProgram.programId,
                    },
                }),
            ], [baseKP]),
        };
    }
    async newWrapper({ hardcap, tokenMint, baseKP = web3_js_1.Keypair.generate(), tokenProgram = token_utils_1.TOKEN_PROGRAM_ID, admin = this.provider.wallet.publicKey, payer = this.provider.wallet.publicKey, }) {
        const [mintWrapper] = await (0, pda_1.findMintWrapperAddress)(baseKP.publicKey, this.program.programId);
        return {
            mintWrapper,
            tx: this.provider.newTX([
                this.program.instruction.newWrapperV2(hardcap, {
                    accounts: {
                        base: baseKP.publicKey,
                        mintWrapper,
                        admin,
                        tokenMint,
                        tokenProgram,
                        payer,
                        systemProgram: web3_js_1.SystemProgram.programId,
                    },
                }),
            ], [baseKP]),
        };
    }
    async newWrapperAndMint({ mintKP = web3_js_1.Keypair.generate(), decimals = 6, ...newWrapperArgs }) {
        const provider = this.provider;
        const { mintWrapper, tx: initMintProxyTX } = await this.newWrapper({
            ...newWrapperArgs,
            tokenMint: mintKP.publicKey,
        });
        const initMintTX = await (0, token_utils_1.createInitMintInstructions)({
            provider,
            mintAuthority: mintWrapper,
            freezeAuthority: mintWrapper,
            mintKP,
            decimals,
        });
        return {
            mintWrapper,
            mint: mintKP.publicKey,
            tx: initMintTX.combine(initMintProxyTX),
        };
    }
    /**
     * Fetches info on a Mint Wrapper.
     * @param minter
     * @returns
     */
    async fetchMintWrapper(wrapper) {
        const accountInfo = await this.provider.connection.getAccountInfo(wrapper);
        if (!accountInfo) {
            return null;
        }
        return this.program.coder.accounts.decode("MintWrapper", accountInfo.data);
    }
    /**
     * Fetches info on a minter.
     * @param minter
     * @returns
     */
    async fetchMinter(wrapper, authority) {
        const [minterAddress] = await (0, pda_1.findMinterAddress)(wrapper, authority, this.program.programId);
        const accountInfo = await this.provider.connection.getAccountInfo(minterAddress);
        if (!accountInfo) {
            return null;
        }
        return this.program.coder.accounts.decode("Minter", accountInfo.data);
    }
    async newMinterV1(wrapper, authority) {
        const [minter, bump] = await (0, pda_1.findMinterAddress)(wrapper, authority, this.program.programId);
        return this.provider.newTX([
            this.program.instruction.newMinter(bump, {
                accounts: {
                    auth: {
                        mintWrapper: wrapper,
                        admin: this.provider.wallet.publicKey,
                    },
                    newMinterAuthority: authority,
                    minter,
                    payer: this.provider.wallet.publicKey,
                    systemProgram: web3_js_1.SystemProgram.programId,
                },
            }),
        ]);
    }
    async newMinter(wrapper, authority) {
        const [minter] = await (0, pda_1.findMinterAddress)(wrapper, authority, this.program.programId);
        return this.provider.newTX([
            this.program.instruction.newMinterV2({
                accounts: {
                    auth: {
                        mintWrapper: wrapper,
                        admin: this.provider.wallet.publicKey,
                    },
                    newMinterAuthority: authority,
                    minter,
                    payer: this.provider.wallet.publicKey,
                    systemProgram: web3_js_1.SystemProgram.programId,
                },
            }),
        ]);
    }
    /**
     * Updates a minter's allowance.
     * @param minter
     * @param allowance
     * @returns
     */
    async minterUpdate(wrapper, authority, allowance) {
        const [minter] = await (0, pda_1.findMinterAddress)(wrapper, authority, this.program.programId);
        return this.provider.newTX([
            this.program.instruction.minterUpdate(allowance, {
                accounts: {
                    auth: {
                        mintWrapper: wrapper,
                        admin: this.provider.wallet.publicKey,
                    },
                    minter,
                },
            }),
        ]);
    }
    /**
     * Creates a new Minter with an allowance.
     * @param wrapper
     * @param authority
     * @param allowance
     * @returns
     */
    async newMinterWithAllowance(wrapper, authority, allowance) {
        const newMinter = await this.newMinter(wrapper, authority);
        const updateAllowance = await this.minterUpdate(wrapper, authority, allowance);
        return newMinter.combine(updateAllowance);
    }
    transferAdmin(wrapper, nextAdmin) {
        return this.provider.newTX([
            this.program.instruction.transferAdmin({
                accounts: {
                    mintWrapper: wrapper,
                    admin: this.provider.wallet.publicKey,
                    nextAdmin,
                },
            }),
        ]);
    }
    acceptAdmin(wrapper) {
        return this.provider.newTX([
            this.program.instruction.acceptAdmin({
                accounts: {
                    mintWrapper: wrapper,
                    pendingAdmin: this.provider.wallet.publicKey,
                },
            }),
        ]);
    }
    /**
     * Mints tokens to an address as a Minter on the Mint Wrapper.
     */
    async performMintTo({ amount, mintWrapper, minterAuthority = this.provider.wallet.publicKey, destOwner = this.provider.wallet.publicKey, }) {
        const ata = await (0, token_utils_1.getOrCreateATA)({
            provider: this.provider,
            mint: amount.token.mintAccount,
            owner: destOwner,
        });
        const [minter] = await (0, pda_1.findMinterAddress)(mintWrapper, minterAuthority);
        return this.sdk.provider.newTX([
            ata.instruction,
            this.program.instruction.performMint(amount.toU64(), {
                accounts: {
                    mintWrapper,
                    minterAuthority,
                    tokenMint: amount.token.mintAccount,
                    destination: ata.address,
                    minter,
                    tokenProgram: token_utils_1.TOKEN_PROGRAM_ID,
                },
            }),
        ]);
    }
    /**
     * Performs a mint of tokens to an account.
     * @deprecated use {@link performMintWithMinter}
     * @returns
     */
    async performMint({ amount, minter, }) {
        return await this.performMintWithMinter({
            amount,
            minter: {
                publicKey: minter.accountId,
                account: minter.accountInfo.data,
            },
        });
    }
    /**
     * Performs a mint of tokens to an account.
     * @returns
     */
    async performMintWithMinter({ amount, minter, }) {
        const minterData = minter.account;
        const ata = await (0, token_utils_1.getOrCreateATA)({
            provider: this.provider,
            mint: amount.token.mintAccount,
            owner: this.provider.wallet.publicKey,
        });
        return this.provider.newTX([
            ata.instruction,
            this.program.instruction.performMint(amount.toU64(), {
                accounts: {
                    mintWrapper: minterData.mintWrapper,
                    minterAuthority: minterData.minterAuthority,
                    tokenMint: amount.token.mintAccount,
                    destination: ata.address,
                    minter: minter.publicKey,
                    tokenProgram: token_utils_1.TOKEN_PROGRAM_ID,
                },
            }),
        ]);
    }
}
exports.MintWrapper = MintWrapper;
//# sourceMappingURL=mintWrapper.js.map